import React from 'react';
import { Button } from '@spark/ui-components/components/Button';
import Icon from '@spark/ui-components/components/Icon';
import Tooltip from '@spark/ui-components/components/Tooltip';
import TableBadge from '@spark/ui-components/components/TanStackTable/components/TableBadge';
import TruncatedText from '@spark/ui-components/components/TanStackTable/components/TruncatedText';

/**
 * Badge simples para status (OK / PENDENTE / outros)
 * Tabela aceita somente: success, danger, default
 */
const getStatusBadgeVariant = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v) return 'default';
  if (v.includes('ok') || v === '1') return 'success';
  if (v.includes('pend')) return 'danger';
  return 'danger';
};

const getStatusBadgeStyle = (value) => {
  const v = String(value || '').trim().toLowerCase();
  if (!v) {
    return { backgroundColor: '#e9ecef', color: '#495057', border: '1px solid #ced4da' };
  }
  if (v.includes('finalizado') || v === 'ok' || v === '1') {
    return { backgroundColor: '#d4edda', color: '#155724', border: '1px solid #c3e6cb' };
  }
  if (v.includes('pend') || v.includes('aguardando')) {
    return { backgroundColor: '#f8d7da', color: '#721c24', border: '1px solid #f5c6cb' };
  }
  if (v.includes('coleta')) {
    return { backgroundColor: '#d1ecf1', color: '#0c5460', border: '1px solid #bee5eb' };
  }
  if (v.includes('baixado')) {
    return { backgroundColor: '#fff3cd', color: '#856404', border: '1px solid #ffeeba' };
  }
  return { backgroundColor: '#e2e3e5', color: '#383d41', border: '1px solid #d6d8db' };
};

const getStatusActionConfig = (value) => {
  const v = String(value || '').trim().toLowerCase();
  const defaultConfig = { iconName: 'package-variant-closed', tooltip: 'Editar status do container' };

  if (!v) return null;
  return defaultConfig;
};

export const getDepotsColumns = (onEditar, onExcluir, onStatusClick) => {
  const columns = [
    {
      id: 'status',
      header: 'Status',
      accessorFn: (row) => row?.Status ?? row?.status,
      size: 320,
      enableSorting: true,
      cell: ({ getValue, row }) => {
        const value = getValue();
        const displayValue = String(value ?? '').replace(/_/g, ' ').toUpperCase();
        const style = getStatusBadgeStyle(value);
        const actionConfig = getStatusActionConfig(value);
        const id = row?.original?.id;

        return (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
            {onStatusClick && actionConfig && id ? (
              <Tooltip dir="top" text={actionConfig.tooltip}>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  aria-label={actionConfig.tooltip}
                  onClick={(e) => {
                    e.stopPropagation();
                    onStatusClick(id);
                  }}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 28,
                    height: 28,
                    padding: 0,
                    flexShrink: 0,
                    backgroundColor: '#fff',
                  }}
                >
                  <Icon name={actionConfig.iconName} size={14} />
                </Button>
              </Tooltip>
            ) : null}

            <span
              style={{
                display: 'inline-block',
                padding: '2px 8px',
                borderRadius: 3,
                fontSize: 12,
                fontWeight: 600,
                lineHeight: 1.4,
                maxWidth: onStatusClick && actionConfig ? 250 : 300,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                ...style,
              }}
              title={displayValue}
            >
              {displayValue}
            </span>
          </div>
        );
      },
    },
    {
      id: 'tipoDepot',
      header: 'Tipo de Registro',
      accessorKey: 'tipoDepot',
      size: 160,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    },
    {
      id: 'numeroContainer',
      header: 'Container',
      accessorKey: 'numeroContainer',
      size: 160,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    },
    {
      id: 'tipoContainer',
      header: 'Tipo',
      accessorKey: 'tipoContainer',
      size: 80,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={6} />,
    },
    {
      id: 'referenciaCliente',
      header: 'Referência Cliente',
      accessorKey: 'referenciaCliente',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={20} />,
    },

    {
      id: 'localColeta',
      header: 'Origem',
      accessorKey: 'localColeta',
      size: 150,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    },
    {
      id: 'destino',
      header: 'Destino',
      accessorKey: 'destino',
      size: 150,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    },
    {
      id: 'dataColeta',
      header: 'Data Coleta',
      accessorKey: 'dataColeta',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue()}</span>,
    },
    {
      id: 'dataEntrega',
      header: 'Data Entrega',
      accessorKey: 'dataEntrega',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue()}</span>,
    },
    {
      id: 'dataDevolucao',
      header: 'Data Devolução',
      accessorKey: 'dataDevolucao',
      size: 190,
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue()}</span>,
    },
    {
      id: 'localDevolucao',
      header: 'Local Devolução',
      accessorKey: 'localDevolucao',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />,
    },
    {
      id: 'freeTime',
      header: 'Data Free time',
      accessorKey: 'freeTime',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <span>{getValue()}</span>,
    },
    {
      id: 'statusPedido',
      header: 'Status Pedido',
      accessorKey: 'statusPedido',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }) => (
        <TableBadge label={getValue()} variant={getStatusBadgeVariant(getValue())} size="sm" />
      ),
    },
/*     {
      id: 'statusDepot',
      header: 'Status Prog.',
      accessorKey: 'statusDepot',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }) => (
        <TableBadge label={getValue()} variant={getStatusBadgeVariant(getValue())} size="sm" />
      ),
    }, */
/*     {
      id: 'statusCTNR',
      header: 'Status CTNR',
      accessorKey: 'statusCTNR',
      size: 140,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    }, */
    {
      id: 'motoristaColeta',
      header: 'Mot. Coleta',
      accessorKey: 'motoristaColeta',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />,
    },
    {
      id: 'veiculoColeta',
      header: 'Veíc. Coleta',
      accessorKey: 'veiculoColeta',
      size: 150,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    },
    {
      id: 'motoristaEntrega',
      header: 'Mot. Entrega',
      accessorKey: 'motoristaEntrega',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />,
    },
    {
      id: 'veiculoEntrega',
      header: 'Veíc. Entrega',
      accessorKey: 'veiculoEntrega',
      size: 170,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    },
    {
      id: 'motoristaDevolucao',
      header: 'Mot. Devolução',
      accessorKey: 'motoristaDevolucao',
      size: 180,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={18} />,
    },
    {
      id: 'veiculoDevolucao',
      header: 'Veíc. Devolução',
      accessorKey: 'veiculoDevolucao',
      size: 170,
      enableSorting: true,
      cell: ({ getValue }) => <TruncatedText value={getValue()} maxLength={14} />,
    },
  ];

  if (onEditar || onExcluir || onStatusClick) {
    columns.push({
      id: 'acoes',
      header: 'Ações',
      accessorKey: 'id',
      size: 130,
      enableSorting: false,
      cell: ({ row }) => {
        const id = row?.original?.id;
       /*  const statusColeta = !!row?.original?.statusColeta;
        const statusEntrega = !!row?.original?.statusEntrega;
        const statusDevolucao = !!row?.original?.statusDevolucao;
        const statusBaixado = !!row?.original?.statusBaixadoCheio; */

        /* const renderStatusButton = (statusKey, label, iconName, isActive) => (
          <Tooltip dir="top" text={`${label}: ${isActive ? 'Concluído' : 'Não Concluído'}`}>
            <Button
              variant={isActive ? 'success' : 'warning'}
              size="sm"
              aria-pressed={isActive}
              onClick={(e) => {
                e.stopPropagation();
                if (onToggleStatus) onToggleStatus(id, statusKey);
              }}
              style={{ marginRight: 0 }}
            >
              <Icon name={iconName} size={14} />
            </Button>
          </Tooltip>
        );
 */
        return (
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-start' }}>

{/*             {onToggleStatus && (
              <>
                {renderStatusButton('coleta', 'Coleta', 'truck', statusColeta)}
                {renderStatusButton('baixado', 'Baixado', 'inbox-arrow-down', statusBaixado)}
                {renderStatusButton('entrega', 'Entrega', 'truck-delivery', statusEntrega)}
                {renderStatusButton('devolucao', 'Devolucao', 'package-variant-closed', statusDevolucao)}
              </>
            )} */}
            {onEditar && (
              <Tooltip dir="top" text="Anexar recibo">
                <Button
                  variant="outline-secondary"
                  size="sm"
                  aria-label="Anexar recibo"
                  onClick={(e) => {
                    e.stopPropagation();
                    onEditar(id);
                  }}
                  style={{ backgroundColor: '#fff' }}
                >
                  <Icon name="paperclip" size={14} />
                </Button>
              </Tooltip>
            )}
          </div>
        );
      },
    });
  }

  return columns;
};
